import React from 'react';
import { render } from 'react-dom';
import lazyLoad from './lazy-loader';

import Request from 'request';
import AppRequest from './lib/appRequest.js';
import GlobalStyle from '../../clad/src/components/Fields/Inputs/CodeEditor/aceEditorGlobalStyles';

////////////////////////////////////////////////////////////////////////////////////////////////
import { ReactQueryDevtools } from 'react-query/devtools';
///////////////////////////////////////////////////////////////////////////////////////////////

import { QueryClientProvider } from 'react-query';
import { queryClient } from './queryClient.js';
import { translate } from './i18n.js';

const App = lazyLoad(() => import('app/components/index.jsx'));
const LoginPage = lazyLoad(() => import('app/components/Login/LoginPage.jsx'));

let parentPanel;

function destroyPreviousComps(bodyClass) {
    document.body.className = bodyClass;

    if (parentPanel) {
        parentPanel.removeAll();
        parentPanel.destroy();
        parentPanel = null;
    }
}

function renderExtComponent(comp) {
    parentPanel =
        parentPanel ||
        new Ext.Panel({
            renderTo: Cla.app.extjsElement,
            height: Cla.app.extjsElement.offsetHeight,
            width: 'auto',
            cls: 'custom-xpanel',
            defaults: { autoScroll: true },
            layout: 'fit',
            items: comp
        });

    if (Cla.app.compCallBack) {
        Cla.app.compCallBack();
    }

    Cla.app.loading(false);
}

async function loadCla(store, user, options, permissions) {
    Ext.QuickTips.init();

    Cla.resizeExtCompFromReact = function(width, height) {
        if (parentPanel) {
            parentPanel.setHeight(height);
            parentPanel.setWidth(width);
            parentPanel.doLayout();
        }
    };

    // expose new app store to Cla
    // and Cla to new app store
    Cla.app = store;
    store.Cla = Cla;

    const { default: ClaPrefs } = await import('common/lib/Prefs.js');

    Cla.user_prefs = window.Prefs = new ClaPrefs({
        username: user.username,
        language: user.language_pref,
        date_format: user.date_format_pref || 'format_from_local',
        time_format: user.time_format_pref || 'HH:mm',
        timezone: user.timezone_pref || 'server_timezone',
        decimal: user.decimal || 'Comma',
        server_timezone: options.serverTimeZone,
        authUrl: options.authUrl,
        autotab_mode: user.autotab_mode || 'same',
        ts_in_activity: user.ts_in_activity ?? false,
        show_empty_fields_pref: user.show_empty_fields_pref ?? '1',
        site: {},
        menus: [],
        stash: {
            theme_dir: '/static/dist/clarive-theme'
        }
    });

    Cla.editionSE = false; // deprecated, for now force false
    Cla.editionEE = true;

    Cla.Request = Request;

    var current = '';

    Cla.ErrorWindow = function(obj) {
        console.error('error in app');
        console.error('error object', obj);

        store.loading(false);

        this.show = function() {};

        if (!obj) {
            Cla.app.renderError(null, 200); // Unknown error
        } else if (obj.msg && obj.msg.length > 0) {
            if (obj.jsError) {
                Cla.app.renderError(obj.msg, 200);
            } else {
                Cla.app.renderError(obj.msg, 500);
            }
        } else if (obj.xhr && obj.xhr.status && obj.xhr.statusText) {
            Cla.app.renderError(obj.xhr.statusText, obj.xhr.status);
        }
    };

    Cla.ui.tab.setTitle = function() {};
    Cla.ui.tab.setTooltip = function() {};
    Cla.ui.tab.closeActive = function() {};

    Cla.explorer = {
        $bigmenu: {
            reloadNodeByMoniker: function() {},
            getNodeById: function() {},
            refresh_node: function() {}
        }
    };

    var openTopic = Cla.ui.tab.openTopic;
    var open = Cla.ui.tab.open;

    Cla.ui.tab.openTopic = function(mid) {
        const args = {
            type: 'open',
            args: [
                'compAjax',
                {
                    url: '/topic/view',
                    topic_mid: String(mid)
                }
            ]
        };
        store.postMessage(args);
    };

    Cla.ui.tab.open = function() {
        const args = [].slice.call(arguments);

        const callStack = {
            type: 'open',
            args: args
        };

        store.postMessage(callStack);
    };

    Cla.ui.tab.refresh = function() {
        Cla.postMessage(current);
    };

    Cla.postMessage = function(data) {
        current = data;

        destroyPreviousComps(data.bodyClass);

        if (data.type === 'destroy') {
            return;
        }

        store.loading(true);

        data.args[1].__c = Math.random();
        if (data.type === 'open') {
            open.apply(Cla.ui.tab, data.args);
        } else if (data.type === 'openTopic') {
            openTopic.apply(Cla.ui.tab, data.args);
        }
    };

    Cla.help_menu = new Ext.menu.Menu({
        cls: 'x-help-menu-items ui-help-menu',
        fresh_menu: true,
        items: []
    });

    Cla.help_button = new Ext.Button({
        icon: '/static/images/icons/lightbulb-off.svg',
        cls: 'x-btn-icon ui-help-button',
        hidden: false,
        tooltip: _('Clarive Help'),
        menu: Cla.help_menu
    });

    Cla.getVersion = (scheduleNext = false) => {
        AppRequest.get('version', {}).then(res => {
            if (res) {
                store.version = res.version;
            }
        });

        if (scheduleNext) {
            setTimeout(() => {
                Cla.getVersion(true);
            }, 600000); // every hour
        }
    };

    Cla.tabpanel = function() {
        return {
            changeTabIcon: icon => {}
        };
    };

    Cla.proxyMainPanel = new Ext.Panel({
        id: 'main-panel',
        add: function(comp) {
            renderExtComponent(comp);
            return {
                setTitle: function() {}
            };
        },
        setActiveTab: function() {},
        getActiveTab: function() {
            return {
                body: Ext.fly('main-content'),
                getEl: function() {
                    return Ext.fly('main-panel');
                },
                on: function() {}
            };
        },
        getId: function() {},
        getInfo: function() {},
        changeTabIcon: function() {},
        remove: function(node, type) {
            Cla.ui.tab.closeActive(type);
        }
    });

    // Cla.DEBUG = true;

    moment.locale(Prefs.language);
    Cla.moment = moment;

    // tell our app that cla and extjs has been
    // fully loaded so we can start the navigator
    store.claScriptsLoaded();
}

const startup = appData => {
    const {
        logon,
        user,
        options,
        permissions,
        acronyms,
        i18n,
        license
    } = appData;

    if (options && options.appName) document.title = options.appName;

    global._ = translate.bind(null, i18n); // eslint-disable-line
    global._loc = global._;
    global.topicAcronyms = acronyms;

    if (logon || !user) {
        render(<LoginPage />, document.getElementById('root'));
    } else {
        import('./store.js').then(Store => {
            const DefaultStore = Store.default;
            const AppStore = new DefaultStore(
                user,
                permissions,
                options,
                license
            );

            Cla.DEBUG = options.debug;

            render(
                <React.Fragment>
                    <GlobalStyle />
                    <QueryClientProvider client={queryClient}>
                        <App store={AppStore} />
                        <ReactQueryDevtools initialIsOpen={false} />
                    </QueryClientProvider>
                </React.Fragment>,
                document.getElementById('root')
            );

            AppStore.setPubsub();

            const lang = user.language_pref || 'en';
            // ExtJS locale files reference the global `Ext` (Ext.UpdateManager)
            // at eval time. Under Vite the locale is a standalone dynamic chunk,
            // so ensure the ext aggregator (which sets window.Ext) has fully
            // loaded before importing the locale — otherwise the locale throws
            // `ReferenceError: Ext is not defined`, surfaced by the browser as
            // "error loading dynamically imported module".
            import('common/vendor/ext.js').then(() => {
                import(`common/vendor/ext/ext-lang-${lang}.js`).catch(err => {
                    console.error(
                        `Error: ${err}.language=${lang}. Fallback to english`
                    );
                    import(`common/vendor/ext/ext-lang-en.js`);
                });
            });

            // ExtJS 3.4 detects DOM-ready via a DOMContentLoaded listener.
            // Under Vite, `Ext` is a lazy chunk that can finish evaluating
            // AFTER the document has already reached `readyState === 'complete'`
            // — notably inside the topic preview <iframe>, whose small document
            // loads before the Ext chunk. In that case Ext misses the already
            // -fired DOMContentLoaded, `Ext.isReady` stays false forever, so
            // `Ext.onReady` never fires and `loadCla` (which sets `Cla.app`)
            // never runs → the preview spins forever. Force Ext's doc-ready
            // when the document is already complete. Same timing-fix family as
            // the ext-lang locale import above. (Under Webpack this never
            // happened: Ext was in the synchronous main bundle, evaluated while
            // the document was still loading.)
            if (
                document.readyState === 'complete' &&
                !Ext.isReady &&
                Ext.EventManager &&
                Ext.EventManager.fireDocReady
            ) {
                Ext.EventManager.fireDocReady();
            }

            Ext.onReady(function() {
                loadCla(AppStore, user, options, permissions);
            });
        });
    }
};

export { startup };
